import React, { useEffect, useState, useMemo } from "react";
import * as Talk from "talkjs";
import { Popup, Session } from "@talkjs/react";
import getEnvVariable from "../../env";
import API from "utils/API";
import useUserCustomerContext from "context/UserCustomer/useUserCustomerContext";
import { usePrivateLabel } from "context/PrivateLabelContext/UsePrivateLabelContextProvider";
import useUser from "context/UserCustomer/useUserCustomerContext";

const Chatbot: React.FC = () => {
  const { loan } = useUserCustomerContext();
  const { privateLabel } = usePrivateLabel();
  const { user } = useUser();
  const [showPopup, setShowPopup] = useState(false);

  const visitorId = useMemo(() => {
    if (user?.id) {
      const id = `visitor-${user.id}`;
      localStorage.setItem("visitorId", id);
      return id;
    }

    const stored = localStorage.getItem("visitorId");
    if (stored) return stored;

    const newId = `visitor-${crypto.randomUUID()}`;
    localStorage.setItem("visitorId", newId);
    return newId;
  }, [user?.id]);

  const conversationId = useMemo(() => {
    if (user?.id) {
      const id = `visitor-conv-${user.id}`;
      localStorage.setItem("conversationId", id);
      return id;
    }

    const stored = localStorage.getItem("conversationId");
    if (stored) return stored;

    const newConvId = `visitor-conv-${crypto.randomUUID()}`;
    localStorage.setItem("conversationId", newConvId);
    return newConvId;
  }, [user?.id]);

  const [currentUser, setCurrentUser] = useState<InstanceType<
    typeof Talk.default.User
  > | null>(null);

  const [botUser, setBotUser] = useState<InstanceType<
    typeof Talk.default.User
  > | null>(null);

  useEffect(() => {
    if (!privateLabel) return;

    let isMounted = true;
    const botName = privateLabel?.lenderName || "Chatbot";

    (Talk.default.ready as Promise<void>).then(() => {
      if (!isMounted) return;

      setCurrentUser(
        new Talk.default.User({
          id: visitorId,
          name: "Visitor",
          welcomeMessage: "Hi! How can we help you?",
          role: "guest_chatbot",
        }),
      );

      setBotUser(
        new Talk.default.User({
          id: `${privateLabel?.lenderName}`,
          name: `${botName} Bot`,
          welcomeMessage: `Hi! I'm your AI assistant. Ask me anything. If you need to speak to a human, click the elipsis (...) above.`,
          role: "guest_chatbot",
          photoUrl: privateLabel?.mediaResources.favicon,
        }),
      );
    });

    return () => {
      isMounted = false;
    };
  }, [visitorId, privateLabel]);

  const syncConversation = useMemo(() => {
    if (!currentUser || !botUser) return undefined;

    return (session: InstanceType<typeof Talk.default.Session>) => {
      const conv = session.getOrCreateConversation(conversationId);
      conv.setParticipant(currentUser);
      conv.setParticipant(botUser);
      conv.setAttributes({
        custom: {
          isFromVisitor: "true",
          botName: `${privateLabel?.lenderName}`,
        },
      });
      return conv;
    };
  }, [conversationId, currentUser, botUser, privateLabel]);

  const addHelpDeskPerson = async (
    event: Talk.default.ConversationActionEvent,
  ) => {
    const conversationId = event?.conversation?.id;
    await API.get({
      url: `/getServiceUnsecured/add-helpdesk-person?conversationId=${conversationId}&botName=${privateLabel?.lenderName}`,
    });
  };

  const getTalkjsToken = async (): Promise<string> => {
    const result = await API.get(
      `/getServiceUnsecured/talkjs-token?userId=${visitorId}`,
    );
    return "error" in result ? "" : (result.data as string);
  };

  const hasAcceptedInitialOffer = loan?.borrowerFlags?.initialOfferAccepted;
  const hasCompletedDisclosures =
    loan?.borrowerSteps?.finalDisclosuresVerification?.status === "success";

  const hideChatbot = hasAcceptedInitialOffer && !hasCompletedDisclosures;

  useEffect(() => {
    const handler = () => setShowPopup(true);
    window.addEventListener("open-chat", handler);

    return () => {
      window.removeEventListener("open-chat", handler);
    };
  }, []);

  if (hideChatbot) return null;

  if (!currentUser || !botUser || !syncConversation) return null;

  return (
    <Session
      appId={getEnvVariable("TALKJS_APP_ID")}
      syncUser={currentUser}
      tokenFetcher={getTalkjsToken}
    >
      <Popup
        syncConversation={syncConversation}
        showCloseInHeader={true}
        onCustomConversationAction={addHelpDeskPerson}
        show={showPopup}
        onClose={() => setShowPopup(false)}
      />
    </Session>
  );
};

export default Chatbot;
